/**
 * faq-accordion.js
 *  <faq-accordion>  one-open-at-a-time accordion for .faq-item__trigger / .faq-item__body pairs.
 *  <faq-directory>  the accordion plus category sidebar navigation, scroll-spy and live search
 *                   (used by the FAQ page).
 */
class FaqAccordion extends HTMLElement {
  connectedCallback() {
    this.onClick = (event) => {
      const trigger = event.target.closest('.faq-item__trigger');
      if (trigger && this.contains(trigger)) this.toggleItem(trigger);
    };
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  toggleItem(trigger) {
    const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
    this.querySelectorAll('.faq-item__trigger').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.querySelectorAll('.faq-item__body').forEach((b) => b.classList.remove('is-open'));
    if (!wasOpen) {
      trigger.setAttribute('aria-expanded', 'true');
      const body = document.getElementById(trigger.getAttribute('aria-controls'));
      if (body) body.classList.add('is-open');
    }
  }
}

class FaqDirectory extends FaqAccordion {
  connectedCallback() {
    super.connectedCallback();
    this.sidebarLinks = Array.from(this.querySelectorAll('.faq-sidebar__link[data-target]'));
    this.groups = Array.from(this.querySelectorAll('.faq-group[id]'));
    this.searchInput = this.querySelector('.faq-search__input');

    this.onSidebarClick = (event) => {
      const link = event.target.closest('.faq-sidebar__link[data-target]');
      if (!link) return;
      this.sidebarLinks.forEach((l) => l.classList.remove('is-active'));
      link.classList.add('is-active');
      const target = document.getElementById(link.getAttribute('data-target'));
      if (target) {
        const top = target.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
    };
    this.addEventListener('click', this.onSidebarClick);

    if (this.groups.length && 'IntersectionObserver' in window) {
      this.spy = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.sidebarLinks.forEach((l) => {
              l.classList.toggle('is-active', l.getAttribute('data-target') === entry.target.id);
            });
          }
        });
      }, { rootMargin: '-20% 0px -60% 0px' });
      this.groups.forEach((g) => this.spy.observe(g));
    }

    if (this.searchInput) {
      this.onSearch = () => this.filter(this.searchInput.value.toLowerCase().trim());
      this.searchInput.addEventListener('input', this.onSearch);
    }
  }

  filter(query) {
    this.querySelectorAll('.faq-item').forEach((item) => {
      const match = !query || item.textContent.toLowerCase().indexOf(query) !== -1;
      item.hidden = !match;
    });
    this.groups.forEach((group) => {
      const anyVisible = Array.from(group.querySelectorAll('.faq-item')).some((i) => !i.hidden);
      group.hidden = !anyVisible;
    });
    const empty = this.querySelector('.faq-search__empty');
    if (empty) empty.hidden = this.groups.some((g) => !g.hidden);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.onSidebarClick);
    if (this.spy) this.spy.disconnect();
    if (this.searchInput && this.onSearch) this.searchInput.removeEventListener('input', this.onSearch);
  }
}

if (!customElements.get('faq-accordion')) customElements.define('faq-accordion', FaqAccordion);
if (!customElements.get('faq-directory')) customElements.define('faq-directory', FaqDirectory);
